// Where the offline store keeps things: IndexedDB in the page, a Map when IndexedDB is refused (some private windows)
// or the writer chose not to keep things on this computer. Every IndexedDB call is guarded: a failure reads as
// "nothing kept" and a write that fails is dropped, so the app always works from memory at worst.

/** A sealed entry as the relay sent it (never opened at rest). */
export interface CachedEntry {
  seq: number;
  doc: string;
  kind: 'snapshot' | 'batch';
  epoch: number;
  lineage: string;
  heads?: string;
  bytes: Uint8Array;
}

export interface Backend {
  readonly persistent: boolean;
  getMeta<T>(key: string): Promise<T | undefined>;
  putMeta(key: string, value: unknown): Promise<boolean>;
  deleteMeta(key: string): Promise<void>;
  /** Meta keys starting with `prefix`. */
  metaKeys(prefix: string): Promise<string[]>;
  entries(account: string, project: string): Promise<CachedEntry[]>;
  /** Adds entries and records the cursor in one transaction. */
  addEntries(account: string, project: string, entries: CachedEntry[], cursorKey: string, cursor: number): Promise<boolean>;
  /** Replaces a project's entries (after a reset or a local compaction). */
  replaceEntries(account: string, project: string, entries: CachedEntry[], cursorKey: string, cursor: number): Promise<boolean>;
  clearEntries(account: string, project?: string): Promise<void>;
}

const META = 'meta';
const ENTRIES = 'entries';

type Key = [string, string, number];
const range = (account: string, project?: string) =>
  project === undefined ? IDBKeyRange.bound([account], [account, []]) : IDBKeyRange.bound([account, project, -Infinity], [account, project, Infinity]);

export class MemoryBackend implements Backend {
  readonly persistent = false;
  private meta = new Map<string, unknown>();
  private rows = new Map<string, Map<number, CachedEntry>>();

  async getMeta<T>(key: string) {
    return structuredClone(this.meta.get(key)) as T | undefined;
  }
  async putMeta(key: string, value: unknown) {
    this.meta.set(key, structuredClone(value));
    return true;
  }
  async deleteMeta(key: string) {
    this.meta.delete(key);
  }
  async metaKeys(prefix: string) {
    return [...this.meta.keys()].filter((k) => k.startsWith(prefix)).sort();
  }
  async entries(account: string, project: string) {
    return [...(this.rows.get(`${account}\n${project}`)?.values() ?? [])].sort((a, b) => a.seq - b.seq);
  }
  async addEntries(account: string, project: string, entries: CachedEntry[], cursorKey: string, cursor: number) {
    const id = `${account}\n${project}`;
    const rows = this.rows.get(id) ?? new Map<number, CachedEntry>();
    for (const e of entries) rows.set(e.seq, e);
    this.rows.set(id, rows);
    this.meta.set(cursorKey, cursor);
    return true;
  }
  async replaceEntries(account: string, project: string, entries: CachedEntry[], cursorKey: string, cursor: number) {
    this.rows.delete(`${account}\n${project}`);
    return this.addEntries(account, project, entries, cursorKey, cursor);
  }
  async clearEntries(account: string, project?: string) {
    for (const id of [...this.rows.keys()]) if (id === `${account}\n${project}` || (project === undefined && id.startsWith(`${account}\n`))) this.rows.delete(id);
  }
}

/** Promise wrappers over one IndexedDB database with a `meta` store and an `entries` store keyed [account, project, seq]. */
export class IdbBackend implements Backend {
  readonly persistent = true;
  private constructor(private readonly db: IDBDatabase) {}

  static async open(name = 'writing-suite-app'): Promise<IdbBackend | null> {
    try {
      if (typeof indexedDB === 'undefined') return null;
      const db = await new Promise<IDBDatabase>((resolve, reject) => {
        const req = indexedDB.open(name, 1);
        req.onupgradeneeded = () => {
          req.result.createObjectStore(META);
          req.result.createObjectStore(ENTRIES);
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
        req.onblocked = () => reject(new Error('IndexedDB blocked'));
      });
      // A deleted account's page deleting this database (PROTOCOL.md §9.6) closes this tab's connection rather than
      // leaving the deletion waiting on it.
      db.onversionchange = () => db.close();
      return new IdbBackend(db);
    } catch {
      return null;
    }
  }

  private tx<T>(stores: string[], mode: IDBTransactionMode, work: (tx: IDBTransaction) => IDBRequest<T> | void): Promise<T | undefined> {
    return new Promise<T | undefined>((resolve, reject) => {
      const tx = this.db.transaction(stores, mode);
      const req = work(tx);
      tx.oncomplete = () => resolve(req ? req.result : undefined);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error ?? new Error('transaction aborted'));
    });
  }

  async getMeta<T>(key: string): Promise<T | undefined> {
    try {
      return (await this.tx<T>([META], 'readonly', (tx) => tx.objectStore(META).get(key))) ?? undefined;
    } catch {
      return undefined;
    }
  }

  async putMeta(key: string, value: unknown): Promise<boolean> {
    try {
      await this.tx([META], 'readwrite', (tx) => void tx.objectStore(META).put(value, key));
      return true;
    } catch {
      return false;
    }
  }

  async deleteMeta(key: string): Promise<void> {
    try {
      await this.tx([META], 'readwrite', (tx) => void tx.objectStore(META).delete(key));
    } catch {
      // nothing kept
    }
  }

  async metaKeys(prefix: string): Promise<string[]> {
    try {
      const keys = await this.tx<IDBValidKey[]>([META], 'readonly', (tx) => tx.objectStore(META).getAllKeys(IDBKeyRange.bound(prefix, `${prefix}￿`)));
      return (keys ?? []).map(String);
    } catch {
      return [];
    }
  }

  async entries(account: string, project: string): Promise<CachedEntry[]> {
    try {
      const rows = await this.tx<CachedEntry[]>([ENTRIES], 'readonly', (tx) => tx.objectStore(ENTRIES).getAll(range(account, project)));
      return (rows ?? []).sort((a, b) => a.seq - b.seq);
    } catch {
      return [];
    }
  }

  private write(account: string, project: string, entries: CachedEntry[], cursorKey: string, cursor: number, replace: boolean) {
    return this.tx([META, ENTRIES], 'readwrite', (tx) => {
      const store = tx.objectStore(ENTRIES);
      if (replace) store.delete(range(account, project));
      for (const e of entries) store.put(e, [account, project, e.seq] satisfies Key);
      tx.objectStore(META).put(cursor, cursorKey);
    });
  }

  async addEntries(account: string, project: string, entries: CachedEntry[], cursorKey: string, cursor: number): Promise<boolean> {
    try {
      await this.write(account, project, entries, cursorKey, cursor, false);
      return true;
    } catch {
      return false;
    }
  }

  async replaceEntries(account: string, project: string, entries: CachedEntry[], cursorKey: string, cursor: number): Promise<boolean> {
    try {
      await this.write(account, project, entries, cursorKey, cursor, true);
      return true;
    } catch {
      return false;
    }
  }

  async clearEntries(account: string, project?: string): Promise<void> {
    try {
      await this.tx([ENTRIES], 'readwrite', (tx) => void tx.objectStore(ENTRIES).delete(range(account, project)));
    } catch {
      // nothing kept
    }
  }
}
